import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Resource delivery is queued in the site database, ready for MailerLite
// groups/automations. One subscriber per email; already-delivered resources
// are never re-triggered.
const ResourceSignupForm = ({ resource, subscriber, onSubscriber }) => {
  const [firstName, setFirstName] = useState(subscriber?.first_name || "");
  const [email, setEmail] = useState(subscriber?.email || "");
  const [wantsCollection, setWantsCollection] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const slug = resource.slug;
  const inputClass =
    "flex-1 bg-transparent border border-stone px-5 py-3.5 text-sm text-charcoal placeholder:text-taupe/70 focus:outline-none focus:border-teal transition-colors duration-500";
  const ctaClass =
    "bg-teal text-ivory text-xs uppercase tracking-[0.25em] px-8 py-3.5 hover:bg-deepteal transition-colors duration-500 disabled:opacity-60";
  const disclosure =
    "You’ll also receive Tara’s Letters — occasional reflections, practices and resources from Tara Aurora. Unsubscribe anytime.";

  const alreadyDelivered =
    subscriber &&
    (subscriber.collection_delivered || (subscriber.delivered_slugs || []).includes(slug));

  const applyResponse = (data) => {
    onSubscriber({
      first_name: data.first_name,
      email: data.email,
      delivered_slugs: data.delivered_slugs || [],
      collection_delivered: !!data.collection_delivered,
    });
    setResult({ collection: !!data.collection, already: data.status === "already_sent" });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!firstName.trim() || !email.trim()) return;
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/resources/request`, {
        first_name: firstName.trim(),
        email: email.trim(),
        slug,
        collection: wantsCollection,
      });
      applyResponse(data);
    } catch {
      toast.error("Something didn’t go through. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const sendKnown = async (collection) => {
    setLoading(true);
    try {
      const { data } = collection
        ? await axios.post(`${API}/resources/collection`, { email: subscriber.email })
        : await axios.post(`${API}/resources/request`, {
            first_name: subscriber.first_name,
            email: subscriber.email,
            slug,
            collection: false,
          });
      applyResponse(data);
    } catch {
      toast.error("Something didn’t go through. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (result) {
    return (
      <div data-testid={`resource-${slug}-success`}>
        <p className="font-serif italic text-xl text-deepteal">
          {result.already
            ? "It’s already on its way to you."
            : result.collection
              ? "Your REGULATE collection is on its way. Check your inbox."
              : "It’s on its way. Check your inbox."}
        </p>
        {!result.collection && subscriber && !subscriber.collection_delivered && (
          <div className="mt-8 border-t border-stone/70 pt-7">
            <p className="text-sm leading-relaxed text-taupe">
              Want the other free REGULATE resources too? Receive the complete
              collection in one email.
            </p>
            <button
              onClick={() => sendKnown(true)}
              disabled={loading}
              data-testid={`resource-${slug}-collection-button`}
              className="mt-5 border border-stone text-charcoal text-xs uppercase tracking-[0.25em] px-8 py-3.5 hover:border-teal hover:text-deepteal transition-colors duration-500 disabled:opacity-60"
            >
              {loading ? "Sending…" : "Send Me the Complete Collection"}
            </button>
          </div>
        )}
      </div>
    );
  }

  if (alreadyDelivered) {
    return (
      <p className="font-serif italic text-xl text-deepteal" data-testid={`resource-${slug}-already`}>
        {subscriber.collection_delivered
          ? "The complete collection is already on its way to you."
          : "It’s already on its way to you."}
      </p>
    );
  }

  if (subscriber) {
    return (
      <div>
        <p className="text-sm text-taupe mb-5">No need to enter your details again.</p>
        <button
          onClick={() => sendKnown(false)}
          disabled={loading}
          data-testid={`resource-${slug}-send-button`}
          className={ctaClass}
        >
          {loading ? "Sending…" : resource.cta}
        </button>
        <p className="mt-5 text-xs leading-relaxed text-taupe/80 max-w-md">{disclosure}</p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} data-testid={`resource-${slug}-form`} className="flex flex-col gap-4 max-w-xl">
      <p className="text-sm text-taupe">
        Enter your details and I’ll send it straight to your inbox.
      </p>
      <div className="flex flex-col sm:flex-row gap-3">
        <label className="sr-only" htmlFor={`resource-${slug}-first-name`}>
          First Name
        </label>
        <input
          id={`resource-${slug}-first-name`}
          data-testid={`resource-${slug}-first-name-input`}
          type="text"
          required
          value={firstName}
          onChange={(e) => setFirstName(e.target.value)}
          placeholder="First Name"
          className={inputClass}
        />
        <label className="sr-only" htmlFor={`resource-${slug}-email`}>
          Email Address
        </label>
        <input
          id={`resource-${slug}-email`}
          data-testid={`resource-${slug}-email-input`}
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Email Address"
          className={inputClass}
        />
      </div>

      <div className="border border-stone/70 bg-white/40 px-5 py-4">
        <p className="text-sm text-charcoal mb-3">
          Want the rest of the free REGULATE resources too?
        </p>
        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={wantsCollection}
            onChange={(e) => setWantsCollection(e.target.checked)}
            data-testid={`resource-${slug}-collection-checkbox`}
            className="mt-0.5 h-4 w-4 accent-teal cursor-pointer"
          />
          <span>
            <span className="block text-sm text-charcoal">
              Yes, send me the complete free resource collection.
            </span>
            <span className="mt-1 block text-xs leading-relaxed text-taupe/80">
              Includes grounding and quiet-mind practices, the feelings guide,
              sleep audio, and an excerpt from REGULATE.
            </span>
          </span>
        </label>
      </div>

      <div>
        <button type="submit" disabled={loading} data-testid={`resource-${slug}-submit-button`} className={ctaClass}>
          {loading ? "Sending…" : resource.cta}
        </button>
      </div>

      <p className="text-xs leading-relaxed text-taupe/80" data-testid={`resource-${slug}-disclosure`}>
        {disclosure}
      </p>
    </form>
  );
};

export default ResourceSignupForm;
